/**
 * A column: its name and its exact count (the server's), what it is, its
 * cards, and "Show more" when the server holds more than are loaded. While a
 * card is being dragged the column says whether a drop here would OPEN an act
 * or be refused, before the card is let go.
 */
import React from 'react';
import { useDroppable } from '@dnd-kit/core';

export type DropHint = 'origin' | 'act' | 'refuse' | null;

export interface KanbanColumnProps {
  columnKey: string;
  label: string;
  what?: string;
  count: number;
  loaded: number;
  hasMore: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** While dragging: what a drop here would do. */
  hint: DropHint;
  /** One line said under the header while dragging over this column. */
  hintText?: string | null;
  emptyText?: string;
  /** What one card is, for the count's accessible name: ['case', 'cases']. */
  unit?: [string, string];
  /** A history column (2026-10-14): drawn quietly apart, its note visible under the name. */
  history?: boolean;
  note?: string;
  children: React.ReactNode;
}

const nf = new Intl.NumberFormat('en-US');

export default function KanbanColumn({
  columnKey, label, what, count, loaded, hasMore, loadingMore = false, onLoadMore, hint, hintText, emptyText = 'Nothing here.', unit = ['record', 'records'], history = false, note, children,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${columnKey}`, data: { column: columnKey } });
  const headId = `okb-col-${columnKey}-h`;
  const whatId = `okb-col-${columnKey}-what`;
  return (
    <section
      ref={setNodeRef}
      className={`okb-col${history ? ' okb-col--history' : ''}${hint ? ` okb-col--${hint}` : ''}${isOver ? ' is-over' : ''}`}
      aria-labelledby={headId}
      aria-describedby={what ? whatId : undefined}
      data-column={columnKey}
    >
      <header className="okb-col__head">
        <h3 id={headId} className="okb-col__name">
          {label}
          <span className="okb-col__count" aria-label={`${nf.format(count)} ${count === 1 ? unit[0] : unit[1]}`}>{nf.format(count)}</span>
        </h3>
        {/* What the column is: said to assistive technology here, and to everyone in the board's "How the columns are decided". */}
        {note && <p className="okb-col__note">{note}</p>}
        {what && <p id={whatId} className="okb-sr">{what}</p>}
        {hint && hintText && <p className="okb-col__hint" aria-hidden="true">{hintText}</p>}
      </header>
      {loaded === 0 ? (
        <p className="okb-col__empty">{count === 0 ? emptyText : 'Loading…'}</p>
      ) : (
        <ul className="okb-col__list" aria-label={`${label}: ${nf.format(loaded)} of ${nf.format(count)} shown`}>
          {children}
        </ul>
      )}
      {hasMore && onLoadMore && (
        <div className="okb-col__more">
          <button type="button" className="okb-btn" onClick={onLoadMore} disabled={loadingMore} aria-describedby={headId}>
            {loadingMore ? 'Loading…' : 'Show more'}
          </button>
          <span className="okb-col__shown">{nf.format(loaded)} of {nf.format(count)}</span>
        </div>
      )}
    </section>
  );
}
